/**
 * Q&A(スタッフページ。2026-09-12 発注者要望「シフト・成績表・Q&A・マニュアル」の1つ。2026-09-17 実装)。
 *  - Q&A: スタッフが見る一覧(Mobile First)。カテゴリで絞り、キーワードで探し、質問を押すと回答が開く
 *  - 管理: admin だけ。追加・修正・削除と CSV 一括(QaManageTab)
 * タブは URL(?tab=)に持つので、戻るボタンで戻れる。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { SearchBox, matchKeyword } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import QaManageTab from './QaManageTab';
import { sortQa } from './qaCsv';
import { fetchQaItems, type QaItem } from './qaRepo';

type Tab = 'view' | 'manage';

export default function QaPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab: Tab = isAdmin && params.get('tab') === 'manage' ? 'manage' : 'view';
  const [items, setItems] = useState<QaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setItems(await fetchQaItems());
    } catch {
      toast('Q&Aの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const go = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p);
  };

  return (
    <div className="screen">
      {isAdmin && (
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <div className="tool-filter">
            <button className={`chip${tab === 'view' ? ' on' : ''}`} onClick={() => go('view')}>
              Q&A
            </button>
            <button className={`chip${tab === 'manage' ? ' on' : ''}`} onClick={() => go('manage')}>
              管理
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      ) : tab === 'manage' ? (
        <QaManageTab items={items} onChanged={() => void reload()} />
      ) : (
        <QaView items={items} />
      )}
    </div>
  );
}

function QaView({ items }: { items: QaItem[] }) {
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const sorted = useMemo(() => sortQa(items), [items]);
  const categories = useMemo(() => [...new Set(sorted.map((q) => q.category))], [sorted]);
  const groups = useMemo(() => {
    const hit = sorted.filter((q) => (category === 'all' || q.category === category) && matchKeyword(keyword, q.category, q.question, q.answer));
    return categories.map((c) => ({ category: c, items: hit.filter((q) => q.category === c) })).filter((g) => g.items.length > 0);
  }, [sorted, categories, category, keyword]);
  const searching = keyword.trim() !== '';

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="card">
      <h3>Q&A</h3>
      <SearchBox value={keyword} onChange={setKeyword} placeholder="キーワードで探す" style={{ width: '100%', height: 38, fontSize: 14, margin: '8px 0 10px' }} />
      {categories.length > 1 && (
        <div className="chips" style={{ marginBottom: 6 }}>
          <button type="button" className={`chip${category === 'all' ? ' on' : ''}`} onClick={() => setCategory('all')}>
            すべて
          </button>
          {categories.map((c) => (
            <button key={c} type="button" className={`chip${category === c ? ' on' : ''}`} onClick={() => setCategory(c)}>
              {c}
            </button>
          ))}
        </div>
      )}
      {groups.length === 0 ? (
        <div className="empty">{items.length === 0 ? 'Q&Aはまだありません' : '該当するQ&Aがありません'}</div>
      ) : (
        groups.map((g) => (
          <section key={g.category} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>
              {g.category}
              <span className="hint" style={{ marginLeft: 6, fontWeight: 400 }}>
                {g.items.length}件
              </span>
            </div>
            {g.items.map((q) => {
              // 検索中は当てはまった回答を開いておく
              const isOpen = searching || open.has(q.id);
              return (
                <div key={q.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <button
                    type="button"
                    onClick={() => toggle(q.id)}
                    aria-expanded={isOpen}
                    style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'flex-start', textAlign: 'left', padding: '12px 2px', background: 'none', border: 'none', color: 'var(--ink)', fontSize: 14, fontWeight: 600, lineHeight: 1.6, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    <span style={{ color: 'var(--brand-blue)', fontWeight: 700 }}>Q</span>
                    <span style={{ flex: 1 }}>{q.question}</span>
                    <span aria-hidden style={{ color: 'var(--muted)', fontWeight: 400 }}>
                      {isOpen ? '−' : '＋'}
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ display: 'flex', gap: 10, padding: '0 2px 14px', fontSize: 14, lineHeight: 1.8 }}>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>A</span>
                      <span style={{ flex: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.answer}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        ))
      )}
    </div>
  );
}
